import { z } from 'zod';
// ============================================================
// Common Schemas (공통 스키마)
// ============================================================
export const ApiSuccessResponseSchema = (dataSchema) => z.object({
    success: z.literal(true),
    data: dataSchema,
    message: z.string().optional(),
});
export const ApiErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.record(z.string(), z.any()).optional(),
    }),
});
export const PaginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    sortBy: z.string().optional(),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export const PaginationMetaSchema = z.object({
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
    totalPages: z.number().int().min(0),
    hasNext: z.boolean(),
    hasPrev: z.boolean(),
});
export const IdParamSchema = z.object({
    id: z.string().cuid2(),
});
export const DateRangeQuerySchema = z.object({
    startDate: z.coerce.date().optional(),
    endDate: z.coerce.date().optional(),
});
export const SearchQuerySchema = z.object({
    q: z.string().min(1).max(100).optional(),
    filter: z.record(z.string(), z.string()).optional(),
});
// ============================================================
// File Upload Schemas
// ============================================================
export const FileUploadResponseSchema = z.object({
    url: z.string().url(),
    filename: z.string(),
    size: z.number().int().min(0),
    mimeType: z.string(),
    uploadedAt: z.date(),
});
// ============================================================
// Validation Helpers
// ============================================================
export const isCuid2 = (value) => {
    return z.string().cuid2().safeParse(value).success;
};
export const isValidUrl = (value) => {
    return z.string().url().safeParse(value).success;
};
export const isValidDateRange = (startDate, endDate) => {
    return startDate <= endDate;
};
export const createSuccessResponse = (data, message) => ({
    success: true,
    data,
    ...(message && { message }),
});
export const createErrorResponse = (code, message, details) => ({
    success: false,
    error: {
        code,
        message,
        ...(details && { details }),
    },
});
// ============================================================
// Error Codes
// ============================================================
export const ERROR_CODES = {
    // Auth
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
    INVALID_TOKEN: 'INVALID_TOKEN',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    AUTH_FAILED: 'AUTH_FAILED',
    ACCOUNT_DELETED: 'ACCOUNT_DELETED',
    INVALID_STATE: 'INVALID_STATE',
    // Validation
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    INVALID_INPUT: 'INVALID_INPUT',
    INVALID_REQUEST: 'INVALID_REQUEST',
    LIMIT_EXCEEDED: 'LIMIT_EXCEEDED',
    // Resources
    NOT_FOUND: 'NOT_FOUND',
    ALREADY_EXISTS: 'ALREADY_EXISTS',
    USER_NOT_FOUND: 'USER_NOT_FOUND',
    // Payment
    PAYMENT_FAILED: 'PAYMENT_FAILED',
    PAYMENT_IN_PROGRESS: 'PAYMENT_IN_PROGRESS',
    INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
    ALREADY_REFUNDED: 'ALREADY_REFUNDED',
    INVALID_STATUS: 'INVALID_STATUS',
    REFUND_PERIOD_EXPIRED: 'REFUND_PERIOD_EXPIRED',
    ACTIVATION_NOT_REFUNDABLE: 'ACTIVATION_NOT_REFUNDABLE',
    CREDITS_USED: 'CREDITS_USED',
    BONUS_CREDITS_USED: 'BONUS_CREDITS_USED',
    // 이벤트·프로모션 보너스를 사용한 결제의 셀프 환불 계정당 횟수 한도 초과 (FIKET-324)
    BONUS_REFUND_LIMIT_REACHED: 'BONUS_REFUND_LIMIT_REACHED',
    // 참조사진 품질 경고 결제의 1회 크레딧 복구 — 자격 없음 / 이미 수령
    QUALITY_RECOVERY_NOT_ELIGIBLE: 'QUALITY_RECOVERY_NOT_ELIGIBLE',
    QUALITY_RECOVERY_ALREADY_CLAIMED: 'QUALITY_RECOVERY_ALREADY_CLAIMED',
    NO_PAYMENT_KEY: 'NO_PAYMENT_KEY',
    TOSS_STATUS_MISMATCH: 'TOSS_STATUS_MISMATCH',
    // AI
    AI_GENERATION_FAILED: 'AI_GENERATION_FAILED',
    MODEL_DISABLED: 'MODEL_DISABLED',
    MISSING_PROMPT: 'MISSING_PROMPT',
    PROMPT_BUILD_FAILED: 'PROMPT_BUILD_FAILED',
    INVALID_SCENES: 'INVALID_SCENES',
    INVALID_REF_PHOTOS: 'INVALID_REF_PHOTOS',
    ALBUM_NOT_FOUND: 'ALBUM_NOT_FOUND',
    THEME_NOT_FOUND: 'THEME_NOT_FOUND',
    // Upload/File
    UPLOAD_FAILED: 'UPLOAD_FAILED',
    INVALID_FILE: 'INVALID_FILE',
    INVALID_TYPE: 'INVALID_TYPE',
    FILE_TOO_LARGE: 'FILE_TOO_LARGE',
    PRESIGN_FAILED: 'PRESIGN_FAILED',
    // Admin
    STYLESHEET_IN_USE: 'STYLESHEET_IN_USE',
    TEMPLATE_IN_USE: 'TEMPLATE_IN_USE',
    IN_USE: 'IN_USE',
    INVALID_DESIGN: 'INVALID_DESIGN',
    DUPLICATE_SLUG: 'DUPLICATE_SLUG',
    // 상품 관리 (FIKET-315)
    PRODUCT_DUPLICATE: 'PRODUCT_DUPLICATE',
    PRODUCT_CHANNEL_CODE_DUPLICATE: 'PRODUCT_CHANNEL_CODE_DUPLICATE',
    FK_DEPENDENCY: 'FK_DEPENDENCY',
    // 권한 그룹에 배정된 사용자가 남아 있어 삭제 불가 (FIKET-417)
    ROLE_HAS_MEMBERS: 'ROLE_HAS_MEMBERS',
    // Rate Limiting
    RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
    // Promotion (시크릿 코드 · 이벤트 쿠폰 · 자동 보너스)
    PROMOTION_INVALID: 'PROMOTION_INVALID',
    PROMOTION_NOT_STARTED: 'PROMOTION_NOT_STARTED',
    PROMOTION_EXPIRED: 'PROMOTION_EXPIRED',
    PROMOTION_EXHAUSTED: 'PROMOTION_EXHAUSTED',
    PROMOTION_ALREADY_USED: 'PROMOTION_ALREADY_USED',
    PROMOTION_NOT_APPLICABLE: 'PROMOTION_NOT_APPLICABLE',
    PROMOTION_DUPLICATE: 'PROMOTION_DUPLICATE',
    PROMOTION_LOCKED: 'PROMOTION_LOCKED',
    PROMOTION_BENEFIT_OVERLAP: 'PROMOTION_BENEFIT_OVERLAP',
    PROMOTION_CLAIM_DISABLED: 'PROMOTION_CLAIM_DISABLED',
    COUPON_UNAVAILABLE: 'COUPON_UNAVAILABLE',
    COUPON_EXPIRED: 'COUPON_EXPIRED',
    COUPON_RESERVED: 'COUPON_RESERVED',
    COUPON_NOT_REVOCABLE: 'COUPON_NOT_REVOCABLE',
    // Invitation
    PREMIUM_REQUIRED: 'PREMIUM_REQUIRED',
    INVITATION_EXPIRED: 'INVITATION_EXPIRED',
    INVITATION_PRIVATE: 'INVITATION_PRIVATE',
    PASSWORD_LOCKED: 'PASSWORD_LOCKED',
    EXTENSION_ALREADY_USED: 'EXTENSION_ALREADY_USED',
    INVALID_PASSWORD: 'INVALID_PASSWORD',
    SLOT_ALREADY_USED: 'SLOT_ALREADY_USED',
    MAX_INVITATIONS_REACHED: 'MAX_INVITATIONS_REACHED',
    // General
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
    /** PDF 렌더 Lambda 미설정(dev·로컬) — HTML 미리보기만 가능 */
    PDF_RENDER_UNAVAILABLE: 'PDF_RENDER_UNAVAILABLE',
    SEARCH_ERROR: 'SEARCH_ERROR',
    DOWNLOAD_ERROR: 'DOWNLOAD_ERROR',
};
//# sourceMappingURL=common.js.map